// src/components/lesson/ProveItButton.tsx
//
// "Prove I know this" — the entry to a step's placement check
// (src/lib/learn/placement-check.ts). The click opens a dialog that states the
// pass rule and the evidence BEFORE anything starts; only "Start" asks the
// server for the questions (POST /api/learn/placement) and goes to the runner.
//
// Rendered by the /learn step cards (on a locked or unpassed step, and as a
// "know it already?" link beside a locked step's blocking prerequisite) and by
// the lesson page's skill check card. The server re-checks everything the
// `placement` prop says, so a stale page can at worst show a button that
// answers with a reason.

'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { BadgeCheck, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  PLACEMENT_RULE,
  placementEvidenceLines,
  placementRuleSentence,
  type PlacementVerdict,
  type StepPlacement,
} from '@/lib/learn/placement-check'
import PlacementVerdictView from './PlacementVerdictView'

export default function ProveItButton({
  topic,
  placement,
  unlocks,
  checkpointCount,
  gatePct,
  variant = 'button',
  label,
  onScored,
}: {
  topic: string
  placement: Pick<StepPlacement, 'offered' | 'open'>
  /** Locked steps a pass would open (unlocksIfPassed). */
  unlocks: string[]
  checkpointCount: number
  gatePct: number
  /** `link`: a small inline text link (a locked step's prerequisite). */
  variant?: 'button' | 'link'
  /** Overrides the trigger text. */
  label?: string
  /**
   * Fired when the dialog closes on a verdict (an unfinished check was scored).
   * router.refresh() re-renders server-rendered surfaces like /learn; a page
   * that loads its path client-side (the lesson page) re-fetches here.
   */
  onScored?: () => void
}) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [verdict, setVerdict] = useState<PlacementVerdict | null>(null)

  if (!placement.offered) return null

  const trigger = label ?? (placement.open ? 'Resume placement check' : 'Prove I know this')

  async function start() {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/learn/placement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic }),
        cache: 'no-store',
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.status === 'started' && typeof data.href === 'string') {
        router.push(data.href)
        return
      }
      if (res.ok && data.status === 'graded' && data.verdict) {
        setVerdict(data.verdict as PlacementVerdict)
        return
      }
      setError(typeof data.error === 'string' ? data.error : 'Could not start the check.')
    } catch {
      setError('Could not start the check. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  function onOpenChange(next: boolean) {
    setOpen(next)
    if (!next && verdict) {
      // An unfinished check was scored: the path behind the dialog is stale.
      setVerdict(null)
      router.refresh()
      onScored?.()
    }
  }

  return (
    <>
      {variant === 'link' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-brand-ink underline underline-offset-2 hover:text-brand-hover"
          data-testid="prove-it-trigger"
        >
          {trigger}
        </button>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="prove-it-trigger"
        >
          <BadgeCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {trigger}
        </Button>
      )}

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prove you know {topic}</DialogTitle>
            <DialogDescription data-testid="prove-it-rule">
              {placementRuleSentence(topic, unlocks)}
            </DialogDescription>
          </DialogHeader>

          {verdict ? (
            <PlacementVerdictView verdict={verdict} />
          ) : (
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
              {placementEvidenceLines(topic, { checkpointCount, gatePct }).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}

          {error && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-300">
              {error}
            </p>
          )}

          {!verdict && (
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                Not now
              </Button>
              <Button
                className="bg-brand text-on-brand hover:bg-brand-hover"
                onClick={() => void start()}
                disabled={busy}
                data-testid="prove-it-start"
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                    Finding fresh questions…
                  </>
                ) : placement.open ? (
                  'Resume the check'
                ) : (
                  `Start · ${PLACEMENT_RULE.questions} questions`
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
